import { useState, useEffect } from "react";
import { axiosInstance } from "../../App";
import { Cake, X, PartyPopper, Gift, Star, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import confetti from 'canvas-confetti';

// Birthday celebration component with animation (for Parents)
export function BirthdayAnimation({ children: birthdayChildren, onClose }) {
  useEffect(() => {
    // Trigger confetti animation
    const duration = 3 * 1000;
    const end = Date.now() + duration;

    const colors = ['#ff69b4', '#ff1493', '#ffd700', '#ff6347', '#9370db'];

    (function frame() {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: colors
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: colors
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    }());
  }, []);

  if (!birthdayChildren || birthdayChildren.length === 0) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-gradient-to-br from-pink-100 via-purple-100 to-yellow-100 rounded-3xl p-6 mx-4 max-w-sm w-full shadow-2xl animate-bounce-in relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
          <Star className="absolute top-4 left-4 w-6 h-6 text-yellow-400 animate-pulse" />
          <Star className="absolute top-8 right-8 w-4 h-4 text-pink-400 animate-pulse" />
          <Star className="absolute bottom-12 left-8 w-5 h-5 text-purple-400 animate-pulse" />
          <PartyPopper className="absolute top-4 right-4 w-8 h-8 text-pink-500 animate-bounce" />
          <Gift className="absolute bottom-4 left-4 w-8 h-8 text-purple-500 animate-bounce" />
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-2 rounded-full bg-white/80 hover:bg-white text-gray-600 z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="text-center relative z-10">
          <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full flex items-center justify-center shadow-lg animate-pulse">
            <Cake className="w-10 h-10 text-white" />
          </div>
          
          <h2 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-purple-600 mb-2">
            🎂 Selamat Hari Jadi! 🎂
          </h2>
          
          <div className="space-y-2 mt-4">
            {birthdayChildren.map((child, idx) => (
              <div 
                key={idx}
                className="bg-white/80 rounded-xl p-3 shadow-md"
              >
                <p className="text-lg font-bold text-purple-700">
                  🎈 {child.name} 🎈
                </p>
                {child.age && (
                  <p className="text-sm text-pink-600 font-medium">
                    Kini berusia {child.age} tahun!
                  </p>
                )}
                {child.class_name && (
                  <p className="text-xs text-gray-500">
                    {child.class_name}
                  </p>
                )}
              </div>
            ))}
          </div>

          <p className="mt-4 text-sm text-purple-600 font-medium">
            🎉 Semoga diberkati dan ceria selalu! 🎉
          </p>

          <button
            onClick={onClose}
            className="mt-4 px-6 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-full font-bold shadow-lg hover:shadow-xl transition-all"
          >
            Terima Kasih! 💖
          </button>
        </div>
      </div>
    </div>
  );
}

// Individual birthday card
function BirthdayCard({ child, index, total }) {
  return (
    <div className="relative">
      {/* Main Card */}
      <div className="bg-white rounded-3xl p-5 shadow-xl border border-pink-100 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-pink-100 to-transparent rounded-bl-full opacity-60" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-purple-100 to-transparent rounded-tr-full opacity-60" />
        
        {/* Sparkle decorations */}
        <Sparkles className="absolute top-3 right-3 w-5 h-5 text-yellow-400 animate-pulse" />
        <Sparkles className="absolute bottom-3 left-3 w-4 h-4 text-pink-400 animate-pulse" style={{ animationDelay: '0.5s' }} />
        
        <div className="relative z-10">
          {/* Top section with avatar and info */}
          <div className="flex items-center gap-4">
            {/* Avatar */}
            <div className="relative">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-pink-400 via-purple-400 to-indigo-400 flex items-center justify-center text-white text-3xl font-black shadow-lg transform rotate-3">
                {child.name?.charAt(0)?.toUpperCase()}
              </div>
              {/* Cake badge */}
              <div className="absolute -bottom-2 -right-2 w-9 h-9 rounded-full bg-gradient-to-br from-yellow-400 to-orange-400 flex items-center justify-center shadow-lg border-3 border-white">
                <span className="text-lg">🎂</span>
              </div>
            </div>
            
            {/* Info */}
            <div className="flex-1 min-w-0">
              <h3 className="font-black text-xl text-gray-800 truncate mb-1">
                {child.name}
              </h3>
              
              {/* Age badge */}
              <div className="inline-flex items-center gap-1.5 bg-gradient-to-r from-pink-500 to-purple-500 text-white text-sm font-bold px-4 py-1.5 rounded-full shadow-md">
                <span>🎈</span>
                <span>{child.age} Tahun</span>
                <span>🎈</span>
              </div>
              
              {child.class_name && (
                <p className="text-xs text-gray-500 mt-2 truncate flex items-center gap-1">
                  <span>📚</span>
                  {child.class_name}
                </p>
              )}
            </div>
          </div>
          
          {/* Divider with confetti */}
          <div className="flex items-center gap-2 my-4">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-pink-300 to-transparent" />
            <span className="text-xl">🎊</span>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-purple-300 to-transparent" />
          </div>
          
          {/* Birthday message */}
          <div className="text-center">
            <p className="text-lg font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
              🎉 Selamat Hari Jadi! 🎉
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Semoga diberkati dan bahagia selalu
            </p>
          </div>
        </div>
      </div>
      
      {/* Page indicator */}
      {total > 1 && (
        <div className="text-center mt-2">
          <span className="text-xs text-gray-400 font-medium">
            {index + 1} / {total}
          </span>
        </div>
      )}
    </div>
  );
}

// Birthday notification carousel component (for Admin/Guru)
export function BirthdayNotificationCard({ children: birthdayChildren, userRole, onViewAll }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!birthdayChildren || birthdayChildren.length === 0) return null;

  const isParent = userRole === 'ibu_bapa';
  
  // For parents, show beautiful card (they also get animation on first load)
  if (isParent) {
    return (
      <div className="mb-6">
        <div className="bg-white rounded-3xl p-5 shadow-xl border border-pink-100 relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-pink-100 via-purple-50 to-transparent rounded-bl-full opacity-70" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-yellow-100 to-transparent rounded-tr-full opacity-70" />
          
          {/* Sparkle decorations */}
          <Sparkles className="absolute top-4 right-4 w-5 h-5 text-yellow-400 animate-pulse" />
          <Star className="absolute bottom-4 left-4 w-4 h-4 text-pink-400 animate-pulse" style={{ animationDelay: '0.5s' }} />
          
          <div className="relative z-10">
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-400 via-purple-400 to-indigo-400 flex items-center justify-center shadow-lg">
                <Cake className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-black text-xl text-gray-800">
                  Hari Jadi! 🎂
                </h3>
                <p className="text-sm text-purple-600 font-medium">Selamat Hari Lahir!</p>
              </div>
              <PartyPopper className="w-8 h-8 text-pink-500 animate-bounce ml-auto" />
            </div>
            
            {/* Children list */}
            <div className="space-y-3">
              {birthdayChildren.map((child, idx) => (
                <div 
                  key={idx} 
                  className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-2xl p-4 border border-pink-200"
                >
                  <div className="flex items-center gap-4">
                    {/* Avatar */}
                    <div className="relative">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white text-2xl font-black shadow-lg">
                        {child.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-gradient-to-br from-yellow-400 to-orange-400 flex items-center justify-center shadow-md border-2 border-white">
                        <span className="text-sm">🎂</span>
                      </div>
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-black text-lg text-gray-800 truncate flex items-center gap-2">
                        <span>🎈</span>
                        {child.name}
                        <span>🎈</span>
                      </h4>
                      <div className="inline-flex items-center gap-1.5 bg-gradient-to-r from-pink-500 to-purple-500 text-white text-sm font-bold px-3 py-1 rounded-full shadow-md mt-1">
                        <span>{child.age} Tahun</span>
                      </div>
                      {child.class_name && (
                        <p className="text-xs text-gray-500 mt-1 truncate">
                          📚 {child.class_name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Message */}
            <div className="mt-4 text-center">
              <p className="text-sm font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
                🎉 Semoga diberkati & bahagia selalu! 🎉
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const goNext = () => {
    if (currentIndex < birthdayChildren.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const goPrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  return (
    <div className="mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center shadow-lg">
            <Cake className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-black text-gray-800 text-lg">
              Hari Jadi Hari Ini
            </h3>
            <p className="text-xs text-gray-500">Jangan lupa ucap selamat! 🎉</p>
          </div>
        </div>
        <div className="bg-gradient-to-r from-pink-500 to-purple-500 text-white text-sm font-bold px-4 py-2 rounded-full shadow-md">
          {birthdayChildren.length} orang
        </div>
      </div>

      {/* Card with navigation */}
      <div className="relative">
        {/* Navigation buttons */}
        {birthdayChildren.length > 1 && (
          <>
            <button
              onClick={goPrev}
              disabled={currentIndex === 0}
              className={`absolute -left-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center border border-gray-100 transition-all ${
                currentIndex === 0 
                  ? 'opacity-40 cursor-not-allowed' 
                  : 'opacity-100 hover:scale-110 active:scale-95'
              }`}
            >
              <ChevronLeft className="w-6 h-6 text-purple-600" />
            </button>
            <button
              onClick={goNext}
              disabled={currentIndex === birthdayChildren.length - 1}
              className={`absolute -right-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center border border-gray-100 transition-all ${
                currentIndex === birthdayChildren.length - 1 
                  ? 'opacity-40 cursor-not-allowed' 
                  : 'opacity-100 hover:scale-110 active:scale-95'
              }`}
            >
              <ChevronRight className="w-6 h-6 text-purple-600" />
            </button>
          </>
        )}

        {/* Card */}
        <div className="px-2">
          <BirthdayCard 
            child={birthdayChildren[currentIndex]} 
            index={currentIndex}
            total={birthdayChildren.length}
          />
        </div>
      </div>

      {/* Dots indicator */}
      {birthdayChildren.length > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {birthdayChildren.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`h-2 rounded-full transition-all duration-300 ${
                idx === currentIndex 
                  ? 'w-8 bg-gradient-to-r from-pink-500 to-purple-500' 
                  : 'w-2 bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Hook to fetch birthday children
export function useBirthdayChildren(user) {
  const [birthdayChildren, setBirthdayChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAnimation, setShowAnimation] = useState(false);

  useEffect(() => {
    const fetchBirthdayChildren = async () => {
      if (!user) return;

      try {
        const response = await axiosInstance.get('/children/birthdays/today');
        const allBirthdays = response.data || [];
        
        let filteredBirthdays = [];

        if (user.role === 'ibu_bapa') {
          // Parents only see their own children
          filteredBirthdays = allBirthdays.filter(child => 
            child.parent_id === user.id || child.parent_phone === user.phone
          );
        } else if (user.role === 'guru') {
          // Teachers see children in their assigned classes
          const teacherClasses = user.assigned_classes || [];
          filteredBirthdays = allBirthdays.filter(child =>
            teacherClasses.includes(child.class_name) || teacherClasses.includes(child.class_id)
          );
        } else if (user.role === 'admin' || user.role === 'superadmin') {
          // Admin sees only children in their branch, Superadmin sees all
          if (user.role === 'admin' && user.branch) {
            filteredBirthdays = allBirthdays.filter(child => child.branch === user.branch);
          } else {
            filteredBirthdays = allBirthdays;
          }
        }

        setBirthdayChildren(filteredBirthdays);

        // Show animation for parents if their child has birthday today
        // Only show once per session
        const sessionKey = `birthday_shown_${new Date().toDateString()}`;
        if (filteredBirthdays.length > 0 && user.role === 'ibu_bapa' && !sessionStorage.getItem(sessionKey)) {
          setShowAnimation(true);
          sessionStorage.setItem(sessionKey, 'true');
        }
      } catch (error) {
        console.error('Error fetching birthdays:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBirthdayChildren();
  }, [user]);

  const closeAnimation = () => setShowAnimation(false);

  return { birthdayChildren, loading, showAnimation, closeAnimation };
}

export default BirthdayNotificationCard;
